/**
 * Experiment Conversion Tracking Utility
 *
 * Tracks conversions for any experiments the user was assigned to.
 * Call this on successful login/signup to attribute conversions to experiment variants.
 */

import Cookies from 'js-cookie';
import { authedFetch } from './api';

const EXPERIMENT_COOKIE_PREFIX = 'experiment_';
const EXPERIMENT_DATA_COOKIE_PREFIX = 'experiment_data_';

/**
 * Track conversion for all active experiment assignments.
 * Call this on login/signup success to attribute the conversion.
 */
export async function trackExperimentConversions(): Promise<void> {
  // Get all cookies
  const cookies = Cookies.get();

  // Find all experiment variant cookies
  const experimentCookies = Object.keys(cookies).filter(
    (key) => key.startsWith(EXPERIMENT_COOKIE_PREFIX) && !key.startsWith(`${EXPERIMENT_COOKIE_PREFIX}data_`)
  );

  if (experimentCookies.length === 0) {
    return; // No experiment assignments to track
  }

  // Track conversion for each experiment
  const trackPromises = experimentCookies.map(async (cookieKey) => {
    const experimentKey = cookieKey.replace(EXPERIMENT_COOKIE_PREFIX, '');
    const dataCookieKey = `${EXPERIMENT_DATA_COOKIE_PREFIX}${experimentKey}`;
    const variantDataStr = cookies[dataCookieKey];

    if (!variantDataStr) {
      return;
    }

    try {
      const variantData = JSON.parse(variantDataStr);

      const payload = JSON.stringify({
        variantId: variantData.id,
        eventType: 'conversion',
        eventData: {
          timestamp: new Date().toISOString(),
          conversion_type: 'login',
        },
      });

      // Needs to survive the page unload that follows a login. sendBeacon did
      // that but cannot set headers, so the CSRF token never accompanied it
      // and the server rejected it with a 403. keepalive gives the same
      // survival with headers intact.
      const experimentId = typeof variantData.id === 'string' ? variantData.id.split('-')[0] : 'unknown';
      await authedFetch(`/api/experiments/${experimentId}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });
    } catch (err) {
      console.error(`Failed to track conversion for experiment ${experimentKey}:`, err);
    }
  });

  await Promise.allSettled(trackPromises);
}

/**
 * Get all active experiment assignments for the current user.
 * Returns array of { experimentKey, variantKey, variantData }
 */
export function getExperimentAssignments(): Array<{
  experimentKey: string;
  variantKey: string;
  variantData: unknown;
}> {
  const cookies = Cookies.get();
  const assignments: Array<{
    experimentKey: string;
    variantKey: string;
    variantData: unknown;
  }> = [];

  const experimentCookies = Object.keys(cookies).filter(
    (key) => key.startsWith(EXPERIMENT_COOKIE_PREFIX) && !key.startsWith(`${EXPERIMENT_COOKIE_PREFIX}data_`)
  );

  for (const cookieKey of experimentCookies) {
    const experimentKey = cookieKey.replace(EXPERIMENT_COOKIE_PREFIX, '');
    const variantKey = cookies[cookieKey];
    const dataCookieKey = `${EXPERIMENT_DATA_COOKIE_PREFIX}${experimentKey}`;
    const variantDataStr = cookies[dataCookieKey];

    if (variantKey && variantDataStr) {
      try {
        const variantData = JSON.parse(variantDataStr);
        assignments.push({ experimentKey, variantKey, variantData });
      } catch {
        // Skip invalid cookies
      }
    }
  }

  return assignments;
}

/**
 * Clear a specific experiment assignment (e.g., after tracking a conversion).
 * Call this after tracking to prevent duplicate conversion tracking.
 */
export function clearExperimentAssignment(experimentKey: string): void {
  Cookies.remove(`${EXPERIMENT_COOKIE_PREFIX}${experimentKey}`);
  Cookies.remove(`${EXPERIMENT_DATA_COOKIE_PREFIX}${experimentKey}`);
}
